import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import MDTypography from "components/MDTypography";

/**
 * Shared page-level error presentation (title + detail), matching billing/customer load styling.
 * Use `title` + `message`, or `message` alone for a single block of text.
 */
export const PAGE_ERROR_TITLES = {
  customerData: "Customer data unavailable",
  customerNotes: "Unable to load customer notes",
  balance: "Balance unavailable",
  customersList: "Unable to load customers",
  accessLogs: "Unable to load access logs",
  paymentHistory: "Unable to load payment history",
  cashTransactions: "Unable to load cash transactions",
  cashRegister: "Unable to load cash register",
  cashRegisterVariance: "Unable to load Over / Short (cash ledger)",
  cashRegisterSave: "Unable to save cash up",
  salesTasks: "Unable to load sales tasks",
  reminders: "Unable to load reminders",
  salesLeads: "Unable to load sales leads",
  salesLead: "Unable to load lead",
  salesLeadEdit: "Unable to update lead",
  salesLeadTasks: "Unable to load lead tasks",
  salesLeadNotes: "Unable to load lead notes",
  salesLeadOpportunities: "Unable to load opportunities",
  towersList: "Unable to load towers",
  tower: "Unable to load tower",
  towerSave: "Unable to save tower",
  ticketsList: "Unable to load tickets",
  contactsList: "Unable to load contacts",
  employeesList: "Unable to load employees",
  employeesSave: "Unable to save employee",
  whatsappInbox: "Unable to load messages",
  notifications: "Unable to load notifications",
  salesOpportunities: "Unable to load sales opportunities",
  salesTask: "Unable to load task",
  ticket: "Unable to load ticket",
  tickets: "Tickets unavailable",
  ticketMessage: "Unable to add message",
  opportunity: "Unable to load opportunity",
  opportunityTasks: "Unable to load opportunity tasks",
  opportunityNotes: "Unable to load opportunity notes",
  contract: "Unable to load contract",
  contractSave: "Unable to save contract",
  lineOfSight: "Line of sight check failed",
  newTicket: "Unable to create ticket",
  authentication: "Unable to sign in",
  setup: "Setup error",
  paymentReceive: "Payment details unavailable",
  subscriptions: "Subscriptions unavailable",
  billingHistory: "Billing history unavailable",
  minimumPayment: "Minimum payment unavailable",
  accountStatus: "Account status unavailable",
  form: "Unable to complete request",
  editCustomer: "Unable to save changes",
  customerAdd: "Unable to continue",
  termsLoad: "Unable to load terms",
  contactDetails: "Contact details error",
  whatsapp: "Unable to send message",
  feasibility: "Feasibility check error",
  generic: "Something went wrong",
  usersAndGroups: "Unable to load users and groups",
};

function PageErrorAlert({ title, message, onDismiss, sx, children, ...rest }) {
  const content = message !== undefined && message !== null ? message : children;
  if (content === null || content === undefined || content === "") {
    return null;
  }

  return (
    <Alert
      severity="error"
      variant="outlined"
      onClose={onDismiss}
      sx={{ width: "100%", textAlign: "left", ...sx }}
      {...rest}
    >
      {title ? (
        <>
          <MDTypography variant="body2" color="error" fontWeight="medium" component="span">
            {title}
          </MDTypography>
          <MDTypography variant="body2" color="text" component="div" mt={0.5}>
            {content}
          </MDTypography>
        </>
      ) : (
        <MDTypography variant="body2" color="text" component="div">
          {content}
        </MDTypography>
      )}
    </Alert>
  );
}

PageErrorAlert.defaultProps = {
  title: null,
  message: undefined,
  onDismiss: undefined,
  sx: undefined,
  children: undefined,
};

PageErrorAlert.propTypes = {
  title: PropTypes.string,
  message: PropTypes.node,
  onDismiss: PropTypes.func,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default PageErrorAlert;
